import qs from 'qs'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { pushWithRegion } from 'actions/NavigationActions'

import TourV2SearchInputs from './TourV2SearchInputs'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import Clickable from 'components/Common/Clickable/BaseClickable'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'

import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import useTourLabels from 'hooks/Tours/useTourLabels'

import GlobalSearchTrackingContext from 'contexts/GlobalSearch/GlobalSearchTracking'
import { OFFER_TYPE_TOUR } from 'constants/offer'
import { getTourGroupTypeSearchMap } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalToursSearch'
import { useAppDispatch } from 'hooks/reduxHooks'
import { trackEvent } from 'analytics/analytics'
import { searchEventWithContext } from 'analytics/snowplow/events'
import { mapGlobalSearchContextToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'

const EditContainer = styled(Clickable)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const StyledDetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${rem(2)};
`

const StyledDescriptionsSection = styled.div`
  display: flex;
  flex-direction: column;
  column-gap: ${rem(8)};
`

interface Props {
  editMenuOnMobile?: boolean
  searchItem?: App.SearchItem
  onSearch?: (searchItem: App.SearchItem) => void
}

function TourV2SearchControls(props: Props) {
  const {
    onSearch,
    editMenuOnMobile = false,
  } = props
  const intl = useIntl()
  const { tourDurationOptions } = useTourLabels()
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const onSearchEvent = useContext(GlobalSearchTrackingContext)

  const openSearchDrawer = useCallback(() => { toggleSearchVertical('tours') }, [toggleSearchVertical])

  const dispatch = useAppDispatch()

  const { searchItem, flexibleNights, flexibleMonths, userSelectedFlexibleMonths, tourGroupTypes = [], dateSearchOptionId } = useContext(GlobalSearchStateContext)

  const handleSearchButtonClick = useCallback(() => {
    const toBeSearchedItem = searchItem ?? ANYWHERE_SEARCH_ITEM

    const tourGroupTypesToQuery = getTourGroupTypeSearchMap(tourGroupTypes)

    const query = qs.stringify({
      destinationId: toBeSearchedItem.value,
      destinationName: toBeSearchedItem.format.mainText,
      flexibleNights: flexibleNights ?? undefined,
      flexibleMonths: flexibleMonths ?? undefined,
      ...tourGroupTypesToQuery,
    })

    trackEvent(searchEventWithContext({
      searchEvent: { type: 'search_bar' },
      sortContext: { value: 'recommended' },
      searchContext: mapGlobalSearchContextToSnowplowSearchEvent({
        searchItem,
        searchVerticals: new Set(['tours']),
        dateSearchOptionId,
        flexibleNights,
      }),
    }))

    dispatch(pushWithRegion(`/search/tours?${query}`))

    onSearch?.(toBeSearchedItem)
    onSearchEvent('submit-button', {
      searchItem,
      vertical: OFFER_TYPE_TOUR,
    })
  }, [searchItem, tourGroupTypes, flexibleNights, flexibleMonths, dateSearchOptionId, dispatch, onSearch, onSearchEvent])

  return <>
    {!editMenuOnMobile && <TourV2SearchInputs
      onSearchButtonClick={handleSearchButtonClick}
      onOpenSearchDrawer={openSearchDrawer}
    />}

    {editMenuOnMobile && <CSSBreakpoint min="tablet">
      <TourV2SearchInputs onSearchButtonClick={handleSearchButtonClick} onOpenSearchDrawer={openSearchDrawer} />
    </CSSBreakpoint>}
    {editMenuOnMobile && <CSSBreakpoint max="mobile">
      <EditContainer onClick={openSearchDrawer}>
        <StyledDetailsSection>
          {searchItem && <Heading variant="heading6">{searchItem.format.mainText}</Heading>}
          <StyledDescriptionsSection>
            {(flexibleNights || flexibleMonths) &&
              <BodyText colour="light-foreground-alt" variant="medium">{prettyFlexibleDates(userSelectedFlexibleMonths ? flexibleMonths : undefined, flexibleNights, tourDurationOptions)}</BodyText>}
          </StyledDescriptionsSection>
        </StyledDetailsSection>
        <TextButton kind="tertiary" endIcon={<LineEditAltIcon />} nonInteractable>{intl.formatMessage({
          id: 'qfNtxknsniGXbWK5Ip55',
          defaultMessage: 'Edit',
          description: 'Button text to edit tour search on mobile on the tour page',
        })}
        </TextButton>
      </EditContainer>
    </CSSBreakpoint>}
  </>
}

export default TourV2SearchControls
